import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Search, Layout, Palette, Settings } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { authFetch } from '@/utils/auth-fetch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Slider } from '@/components/ui/slider';

interface NaturalLanguageSearchSettings {
  enabled: boolean;
  position: 'header' | 'floating' | 'embedded' | 'sidebar';
  style: {
    searchBarStyle: 'minimal' | 'rounded' | 'outlined' | 'filled';
    primaryColor: string;
    backgroundColor: string;
    borderColor: string;
    textColor: string;
    borderRadius: number;
    width: 'auto' | 'full' | 'custom';
    customWidth?: string;
  };
  behavior: {
    placeholder: string;
    autoFocus: boolean;
    showSuggestions: boolean;
    suggestionCount: number;
    searchAsYouType: boolean;
    debounceDelay: number;
    showSearchHistory: boolean;
  };
  results: {
    displayMode: 'dropdown' | 'overlay' | 'inline' | 'sidebar';
    maxResults: number;
    showThumbnails: boolean;
    showExcerpts: boolean;
    excerptLength: number;
    highlightMatches: boolean;
    groupByCategory: boolean;
    showRelevanceScore: boolean;
  };
  floatingButton?: {
    position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
    icon: 'search' | 'custom';
    customIconUrl?: string;
    showLabel: boolean;
    label?: string;
  };
}

interface NaturalLanguageSearchSettingsProps {
  siteId: string;
  isAdmin?: boolean;
}

export function NaturalLanguageSearchSettings({ siteId, isAdmin = false }: NaturalLanguageSearchSettingsProps) {
  const [settings, setSettings] = useState<NaturalLanguageSearchSettings>({
    enabled: true,
    position: 'header',
    style: {
      searchBarStyle: 'rounded',
      primaryColor: '#007bff',
      backgroundColor: '#ffffff',
      borderColor: '#dee2e6',
      textColor: '#333333',
      borderRadius: 8,
      width: 'auto',
    },
    behavior: {
      placeholder: 'Search for anything...',
      autoFocus: false,
      showSuggestions: true,
      suggestionCount: 5,
      searchAsYouType: true,
      debounceDelay: 300,
      showSearchHistory: true,
    },
    results: {
      displayMode: 'dropdown',
      maxResults: 10,
      showThumbnails: true,
      showExcerpts: true,
      excerptLength: 150,
      highlightMatches: true,
      groupByCategory: false,
      showRelevanceScore: false,
    },
    floatingButton: {
      position: 'bottom-right',
      icon: 'search',
      showLabel: false,
      label: 'Search',
    },
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
  }, [siteId]);

  const loadSettings = async () => {
    try {
      const response = await authFetch(`/sites/${siteId}/settings`);
      if (response.ok) {
        const data = await response.json();
        const searchSettings = data.settings?.naturalLanguageSearch;
        if (searchSettings) {
          setSettings(prev => ({ ...prev, ...searchSettings }));
        }
      }
    } catch (error) {
      console.error('Error loading search settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load search settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`/sites/${siteId}/settings`, {
        method: 'PUT',
        body: JSON.stringify({ 
          settings: { 
            naturalLanguageSearch: settings,
            naturalLanguageSearchEnabled: settings.enabled 
          } 
        }),
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Search settings updated successfully',
        });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving search settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save search settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStyleChange = (key: keyof typeof settings.style, value: any) => {
    setSettings(prev => ({
      ...prev,
      style: { ...prev.style, [key]: value }
    }));
  };

  const handleBehaviorChange = (key: keyof typeof settings.behavior, value: any) => {
    setSettings(prev => ({
      ...prev,
      behavior: { ...prev.behavior, [key]: value }
    }));
  };

  const handleResultsChange = (key: keyof typeof settings.results, value: any) => {
    setSettings(prev => ({
      ...prev,
      results: { ...prev.results, [key]: value }
    }));
  };

  if (loading) {
    return <div>Loading search settings...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5" />
          Natural Language Search Configuration
        </CardTitle>
        <CardDescription>
          Configure AI-powered search for your website
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Position Settings */}
        <div className="space-y-3">
          <Label>Search Bar Position</Label>
          <RadioGroup 
            value={settings.position} 
            onValueChange={(value) => setSettings(prev => ({ ...prev, position: value as any }))}
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="header" id="search-header" />
                <Label htmlFor="search-header" className="cursor-pointer">
                  <div className="font-medium">Header</div>
                  <div className="text-xs text-muted-foreground">In page header</div>
                </Label>
              </div>
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="floating" id="search-floating" />
                <Label htmlFor="search-floating" className="cursor-pointer">
                  <div className="font-medium">Floating Button</div>
                  <div className="text-xs text-muted-foreground">Expandable button</div>
                </Label>
              </div>
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="embedded" id="search-embedded" />
                <Label htmlFor="search-embedded" className="cursor-pointer">
                  <div className="font-medium">Embedded</div>
                  <div className="text-xs text-muted-foreground">In page content</div>
                </Label>
              </div>
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="sidebar" id="search-sidebar" />
                <Label htmlFor="search-sidebar" className="cursor-pointer">
                  <div className="font-medium">Sidebar</div>
                  <div className="text-xs text-muted-foreground">Slide-out panel</div>
                </Label>
              </div>
            </div>
          </RadioGroup>
        </div>

        {/* Floating Button Settings (if position is floating) */}
        {settings.position === 'floating' && settings.floatingButton && (
          <div className="space-y-3 p-4 border rounded-lg">
            <Label>Floating Button Settings</Label>
            <div className="space-y-3">
              <div>
                <Label htmlFor="floatingPosition">Button Position</Label>
                <Select 
                  value={settings.floatingButton.position} 
                  onValueChange={(value) => setSettings(prev => ({
                    ...prev,
                    floatingButton: { ...prev.floatingButton!, position: value as any }
                  }))}
                >
                  <SelectTrigger id="floatingPosition">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bottom-right">Bottom Right</SelectItem>
                    <SelectItem value="bottom-left">Bottom Left</SelectItem>
                    <SelectItem value="top-right">Top Right</SelectItem>
                    <SelectItem value="top-left">Top Left</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="showLabel">Show Label</Label>
                <input
                  type="checkbox"
                  id="showLabel"
                  checked={settings.floatingButton.showLabel}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    floatingButton: { ...prev.floatingButton!, showLabel: e.target.checked }
                  }))}
                  className="h-4 w-4"
                />
              </div>
              
              {settings.floatingButton.showLabel && (
                <div>
                  <Label htmlFor="buttonLabel">Button Label</Label>
                  <Input
                    id="buttonLabel"
                    value={settings.floatingButton.label || ''}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      floatingButton: { ...prev.floatingButton!, label: e.target.value }
                    }))}
                    placeholder="Search"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Style Settings */}
        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            <Label>Search Bar Style</Label>
          </div>
          
          <div className="space-y-3">
            <div>
              <Label>Style Preset</Label>
              <Select value={settings.style.searchBarStyle} onValueChange={(value) => handleStyleChange('searchBarStyle', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="minimal">Minimal</SelectItem>
                  <SelectItem value="rounded">Rounded</SelectItem>
                  <SelectItem value="outlined">Outlined</SelectItem>
                  <SelectItem value="filled">Filled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="searchPrimaryColor">Primary Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="searchPrimaryColor"
                    type="color"
                    value={settings.style.primaryColor}
                    onChange={(e) => handleStyleChange('primaryColor', e.target.value)}
                    className="w-20 h-10"
                  />
                  <Input
                    value={settings.style.primaryColor}
                    onChange={(e) => handleStyleChange('primaryColor', e.target.value)}
                    placeholder="#007bff"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="searchBgColor">Background Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="searchBgColor"
                    type="color"
                    value={settings.style.backgroundColor}
                    onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                    className="w-20 h-10"
                  />
                  <Input
                    value={settings.style.backgroundColor}
                    onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <Label>Border Radius (px)</Label>
              <div className="flex items-center gap-4">
                <Slider
                  value={[settings.style.borderRadius]}
                  onValueChange={([value]) => handleStyleChange('borderRadius', value)}
                  min={0}
                  max={50}
                  step={1}
                  className="flex-1"
                />
                <span className="w-12 text-sm">{settings.style.borderRadius}px</span>
              </div>
            </div>
            
            <div>
              <Label>Width</Label>
              <Select value={settings.style.width} onValueChange={(value) => handleStyleChange('width', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto</SelectItem>
                  <SelectItem value="full">Full Width</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {settings.style.width === 'custom' && (
              <div>
                <Label htmlFor="customWidth">Custom Width</Label>
                <Input
                  id="customWidth"
                  value={settings.style.customWidth || ''}
                  onChange={(e) => handleStyleChange('customWidth', e.target.value)}
                  placeholder="e.g., 400px or 50%"
                />
              </div>
            )}
          </div>
        </div>

        {/* Behavior Settings */}
        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            <Label>Search Behavior</Label>
          </div>
          
          <div className="space-y-3">
            <div>
              <Label htmlFor="placeholder">Placeholder Text</Label>
              <Input
                id="placeholder"
                value={settings.behavior.placeholder}
                onChange={(e) => handleBehaviorChange('placeholder', e.target.value)}
                placeholder="Search for anything..."
              />
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="autoFocus">Auto-focus on Load</Label>
                <input
                  type="checkbox"
                  id="autoFocus"
                  checked={settings.behavior.autoFocus}
                  onChange={(e) => handleBehaviorChange('autoFocus', e.target.checked)}
                  className="h-4 w-4"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="searchAsYouType">Search as You Type</Label>
                <input
                  type="checkbox"
                  id="searchAsYouType"
                  checked={settings.behavior.searchAsYouType}
                  onChange={(e) => handleBehaviorChange('searchAsYouType', e.target.checked)}
                  className="h-4 w-4"
                />
              </div>
              
              {settings.behavior.searchAsYouType && (
                <div>
                  <Label>Debounce Delay (ms)</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[settings.behavior.debounceDelay]}
                      onValueChange={([value]) => handleBehaviorChange('debounceDelay', value)}
                      min={100}
                      max={1000}
                      step={50}
                      className="flex-1"
                    />
                    <span className="w-16 text-sm">{settings.behavior.debounceDelay}ms</span>
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <Label htmlFor="showSuggestions">Show Suggestions</Label>
                <input
                  type="checkbox"
                  id="showSuggestions"
                  checked={settings.behavior.showSuggestions}
                  onChange={(e) => handleBehaviorChange('showSuggestions', e.target.checked)}
                  className="h-4 w-4"
                />
              </div>
              
              {settings.behavior.showSuggestions && (
                <div>
                  <Label>Max Suggestions</Label>
                  <Select 
                    value={settings.behavior.suggestionCount.toString()} 
                    onValueChange={(value) => handleBehaviorChange('suggestionCount', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3</SelectItem>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="8">8</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <Label htmlFor="showSearchHistory">Show Search History</Label>
                <input
                  type="checkbox"
                  id="showSearchHistory"
                  checked={settings.behavior.showSearchHistory}
                  onChange={(e) => handleBehaviorChange('showSearchHistory', e.target.checked)}
                  className="h-4 w-4"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Results Display Settings */}
        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center gap-2">
            <Layout className="h-4 w-4" />
            <Label>Results Display</Label>
          </div>
          
          <div className="space-y-3">
            <div>
              <Label>Display Mode</Label>
              <Select value={settings.results.displayMode} onValueChange={(value) => handleResultsChange('displayMode', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dropdown">Dropdown</SelectItem>
                  <SelectItem value="overlay">Full Overlay</SelectItem>
                  <SelectItem value="inline">Inline Results</SelectItem>
                  <SelectItem value="sidebar">Sidebar Panel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Max Results</Label>
              <Select 
                value={settings.results.maxResults.toString()} 
                onValueChange={(value) => handleResultsChange('maxResults', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="showThumbnails">Show Thumbnails</Label>
                <input
                  type="checkbox"
                  id="showThumbnails"
                  checked={settings.results.showThumbnails}
                  onChange={(e) => handleResultsChange('showThumbnails', e.target.checked)}
                  className="h-4 w-4"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="showExcerpts">Show Excerpts</Label>
                <input
                  type="checkbox"
                  id="showExcerpts"
                  checked={settings.results.showExcerpts}
                  onChange={(e) => handleResultsChange('showExcerpts', e.target.checked)}
                  className="h-4 w-4"
                />
              </div>
              
              {settings.results.showExcerpts && (
                <div>
                  <Label>Excerpt Length (characters)</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[settings.results.excerptLength]}
                      onValueChange={([value]) => handleResultsChange('excerptLength', value)}
                      min={50}
                      max={300}
                      step={10}
                      className="flex-1"
                    />
                    <span className="w-12 text-sm">{settings.results.excerptLength}</span>
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <Label htmlFor="highlightMatches">Highlight Matches</Label>
                <input
                  type="checkbox"
                  id="highlightMatches"
                  checked={settings.results.highlightMatches}
                  onChange={(e) => handleResultsChange('highlightMatches', e.target.checked)}
                  className="h-4 w-4"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="groupByCategory">Group by Category</Label>
                <input
                  type="checkbox"
                  id="groupByCategory"
                  checked={settings.results.groupByCategory}
                  onChange={(e) => handleResultsChange('groupByCategory', e.target.checked)}
                  className="h-4 w-4"
                />
              </div>
              
              {isAdmin && (
                <div className="flex items-center justify-between">
                  <Label htmlFor="showRelevanceScore">Show Relevance Score</Label>
                  <input
                    type="checkbox"
                    id="showRelevanceScore"
                    checked={settings.results.showRelevanceScore}
                    onChange={(e) => handleResultsChange('showRelevanceScore', e.target.checked)}
                    className="h-4 w-4"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Preview Alert */}
        <Alert>
          <Search className="h-4 w-4" />
          <AlertDescription>
            <strong>Note:</strong> Natural Language Search uses AI to understand user queries and provide relevant results. 
            Make sure your content is properly indexed for best results.
          </AlertDescription>
        </Alert>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={saveSettings} disabled={saving}>
            {saving ? 'Saving...' : 'Save Search Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}